// features/pos/components/CartPanel.tsx
// ENHANCED - Large Cart Handling, Dynamic Discounts, Better UX, Walk-in Support
// NEW: Optional maxQuantities prop to enforce portion limits (BOM POS)

import { useState, useEffect } from 'react';
import { usePosStore } from '@/store/posStore';
import { useAuthStore } from '@/store/authStore';
import {
    Minus, Plus, Trash2, ShoppingCart, Percent, Tag,
    Receipt, Sparkles, X,
    Coffee, Star, Package, User, Gift,
    ShieldCheck, Shield, AlertCircle, Loader2,
    List, ScrollText,
} from 'lucide-react';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { getDiscountReasons } from '../services/posApi';

// ============================================================
// DISCOUNT OPTION INTERFACE
// ============================================================
interface DiscountOption {
    id: string; name: string; type: 'percentage' | 'fixed';
    maxAmount: number; requiresApproval: boolean;
    icon: React.ElementType; gradient: string; bgLight: string; borderLight: string;
}

// ============================================================
// CART ITEM COMPACT
// ============================================================
function CartItemCompact({ item, onUpdateQuantity, onRemove, onStartDiscount, editingDiscount, discountInput, onDiscountInputChange, onApplyDiscount, onCancelDiscount, maxQuantities }: {
    item: any; onUpdateQuantity: (id: string, qty: number) => void;
    onRemove: (id: string) => void; onStartDiscount: (id: string, amount: number) => void;
    editingDiscount: string | null; discountInput: string;
    onDiscountInputChange: (val: string) => void; onApplyDiscount: (id: string) => void;
    onCancelDiscount: () => void;
    maxQuantities?: Record<string, number>;
}) {
    const formatNaira = (amount: number): string => {
        if (typeof amount !== 'number' || isNaN(amount)) return '₦0.00';
        return `₦${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const maxQty = maxQuantities?.[item.productId];
    const atMax = maxQty !== undefined && item.quantity >= maxQty;

    return (
        <motion.div
            initial={{ opacity: 0, x: 30, height: 0 }}
            animate={{ opacity: 1, x: 0, height: 'auto' }}
            exit={{ opacity: 0, x: -30, height: 0 }}
            className="px-3 py-2 hover:bg-slate-50/80 transition-colors group"
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{item.productName}</p>
                    <div className="flex items-center gap-2 text-[10px] text-slate-400">
                        <span>{formatNaira(item.unitPrice)}</span>
                        {item.discountAmount > 0 && (
                            <span className="text-amber-600 font-medium">-{formatNaira(item.discountAmount)}</span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
                        <button onClick={() => onUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                            className="w-6 h-6 flex items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-rose-500" type="button"><Minus size={12} /></button>
                        <span className="w-7 text-center text-xs font-bold text-slate-900 tabular-nums">{item.quantity}</span>
                        <button
                            onClick={() => {
                                if (!atMax) onUpdateQuantity(item.id, item.quantity + 1);
                            }}
                            disabled={atMax}
                            className={clsx(
                                'w-6 h-6 flex items-center justify-center rounded-md',
                                atMax ? 'bg-slate-100 text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:bg-white hover:text-emerald-500'
                            )}
                            type="button"
                        ><Plus size={12} /></button>
                    </div>
                    <span className="text-xs font-bold text-slate-800 w-16 text-right tabular-nums">
                        {formatNaira((item.totalAmount || item.quantity * item.unitPrice - (item.discountAmount || 0)))}
                    </span>
                    <button onClick={() => onRemove(item.id)}
                        className="p-1 rounded text-slate-300 hover:text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-all" type="button"><Trash2 size={12} /></button>
                </div>
            </div>
            {/* Discount edit inline (unchanged) */}
            {editingDiscount === item.id && (
                <div className="flex items-center gap-1.5 mt-1.5 pl-0">
                    <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">₦</span>
                        <input type="number" value={discountInput} onChange={(e) => onDiscountInputChange(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') onApplyDiscount(item.id); if (e.key === 'Escape') onCancelDiscount(); }}
                            className="w-20 pl-5 pr-2 py-1 border-2 border-amber-300 rounded-lg text-[10px] font-bold focus:ring-2 focus:ring-amber-500/20" autoFocus />
                    </div>
                    <button onClick={() => onApplyDiscount(item.id)} className="px-2 py-1 bg-amber-500 text-white rounded text-[10px] font-bold" type="button">Apply</button>
                    <button onClick={onCancelDiscount} className="px-2 py-1 bg-slate-200 rounded text-[10px]" type="button">✕</button>
                </div>
            )}
            {item.discountAmount === 0 && editingDiscount !== item.id && (
                <button onClick={() => onStartDiscount(item.id, 0)}
                    className="text-[10px] text-slate-400 hover:text-amber-600 mt-0.5 ml-0" type="button">
                    <Percent size={10} className="inline mr-0.5" />Add discount
                </button>
            )}
        </motion.div>
    );
}

// ============================================================
// MAIN CART PANEL
// ============================================================
export function CartPanel({
    onCheckout,
    disabled = false,
    maxQuantities,
}: {
    onCheckout: () => void;
    disabled?: boolean;
    maxQuantities?: Record<string, number>; // productId -> max allowed quantity
}) {
    const {
        cart, subtotal, discountTotal, taxTotal, grandTotal,
        removeItem, updateQuantity, updateDiscount, customerInfo, customerName, customerId, customerVerification, saleType,
        tabs, activeTabId, switchTab, addTab, closeTab,
    } = usePosStore();
    const tenantId = useAuthStore(s => s.user?.tenantId) || '';

    const [showDiscounts, setShowDiscounts] = useState(false);
    const [editingDiscount, setEditingDiscount] = useState<string | null>(null);
    const [discountInput, setDiscountInput] = useState('');
    const [discountOptions, setDiscountOptions] = useState<DiscountOption[]>([]);
    const [compactView, setCompactView] = useState(false);

    const formatNaira = (amount: number): string => {
        if (typeof amount !== 'number' || isNaN(amount)) return '₦0.00';
        return `₦${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    useEffect(() => {
        if (cart.length > 8) setCompactView(true);
        else setCompactView(false);
    }, [cart.length]);

    useEffect(() => {
        if (tenantId) {
            getDiscountReasons(tenantId)
                .then(reasons => {
                    if (reasons && reasons.length > 0) {
                        const mapped: DiscountOption[] = reasons.map(r => ({
                            id: r.id, name: r.name, type: r.type, maxAmount: r.maxAmount || 100,
                            requiresApproval: r.requiresApproval,
                            icon: r.type === 'percentage' ? Percent : Gift,
                            gradient: r.type === 'percentage' ? 'from-amber-500 to-orange-500' : 'from-emerald-500 to-teal-500',
                            bgLight: r.type === 'percentage' ? 'bg-amber-50' : 'bg-emerald-50',
                            borderLight: r.type === 'percentage' ? 'border-amber-200' : 'border-emerald-200',
                        }));
                        setDiscountOptions(mapped);
                    } else {
                        setDiscountOptions([]);
                    }
                })
                .catch(() => { setDiscountOptions([]); });
        }
    }, [tenantId]);

    const handleApplyPreset = (preset: DiscountOption) => {
        if (cart.length === 0) return;
        let discountAmount = 0;
        if (preset.type === 'percentage') {
            discountAmount = subtotal * (preset.maxAmount / 100);
        } else {
            discountAmount = Math.min(preset.maxAmount, subtotal);
        }
        if (cart.length > 0) {
            updateDiscount(cart[0].id, Math.round(discountAmount * 100) / 100);
        }
        setShowDiscounts(false);
    };

    const startEditDiscount = (itemId: string, currentAmount: number) => {
        setEditingDiscount(itemId);
        setDiscountInput(String(currentAmount || ''));
    };

    const handleCustomDiscount = (itemId: string) => {
        const amount = parseFloat(discountInput);
        if (isNaN(amount) || amount < 0) return;
        updateDiscount(itemId, amount);
        setEditingDiscount(null);
        setDiscountInput('');
    };

    const tierColors: Record<string, string> = {
        bronze: 'from-amber-700 to-amber-800', silver: 'from-slate-400 to-slate-500',
        gold: 'from-amber-400 to-yellow-500', platinum: 'from-purple-500 to-indigo-600',
    };

    const tier = customerInfo?.loyaltyTier || 'bronze';

    // MULTI-CART TABS BAR COMPONENT
    const renderCartTabsBar = () => {
        const cartTabs = tabs && tabs.length > 0 ? tabs : [{ id: 'tab-1', name: 'Cart 1', cart: [] }];
        return (
            <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-100/90 border-b border-slate-200 overflow-x-auto flex-shrink-0">
                {cartTabs.map((t, idx) => {
                    const isActive = t.id === (activeTabId || 'tab-1');
                    const itemCount = (t.cart || []).reduce((sum: number, i: any) => sum + i.quantity, 0);
                    return (
                        <div
                            key={t.id}
                            onClick={() => switchTab(t.id)}
                            className={clsx(
                                'group flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap shadow-xs select-none',
                                isActive
                                    ? 'bg-teal-600 text-white shadow-teal-600/20'
                                    : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                            )}
                        >
                            <span>{t.name || `Cart ${idx + 1}`}</span>
                            <span
                                className={clsx(
                                    'text-[10px] px-1.5 py-0.2 rounded-full font-bold',
                                    isActive ? 'bg-teal-700 text-teal-100' : 'bg-slate-100 text-slate-600'
                                )}
                            >
                                {itemCount}
                            </span>
                            {cartTabs.length > 1 && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        closeTab(t.id);
                                    }}
                                    className={clsx(
                                        'p-0.5 rounded-md hover:bg-black/10 transition-colors ml-0.5',
                                        isActive ? 'text-teal-200 hover:text-white' : 'text-slate-400 hover:text-rose-600'
                                    )}
                                    title="Close cart tab"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    );
                })}

                {/* Add New Cart Tab Button */}
                <button
                    type="button"
                    onClick={() => addTab()}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300 border border-dashed border-slate-300 rounded-xl text-xs font-bold text-slate-600 transition-all shadow-xs whitespace-nowrap"
                    title="Open new checkout tab (Park current cart)"
                >
                    <Plus size={13} />
                    <span>New Cart</span>
                </button>
            </div>
        );
    };

    if (cart.length === 0) {
        return (
            <div className="flex flex-col h-full bg-white">
                {renderCartTabsBar()}
                <div className="flex flex-col items-center justify-center flex-1 px-6 py-12">
                    <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} className="mb-6">
                        <div className="w-24 h-24 rounded-3xl bg-slate-100 flex items-center justify-center">
                            <ShoppingCart size={40} strokeWidth={1.5} className="text-slate-300" />
                        </div>
                    </motion.div>
                    <h3 className="text-lg font-bold text-slate-600 mb-2">Cart is Empty</h3>
                    <p className="text-sm text-slate-400 text-center mb-6">Search for products or tap them to add to cart</p>
                    <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
                        <div className="p-3 bg-slate-50 rounded-xl text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Sale Type</p>
                            <span className={clsx('text-sm font-bold capitalize px-2 py-0.5 rounded-full',
                                saleType === 'wholesale' ? 'bg-blue-100 text-blue-700' : saleType === 'clearance' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700')}>{saleType}</span>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-xl text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Tax Rate</p>
                            <p className="text-sm font-bold text-slate-600">% VAT</p>
                        </div>
                    </div>
                    {customerName && (
                        <div className="mt-4 p-3 bg-emerald-50 rounded-xl w-full max-w-xs">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center"><User size={16} className="text-emerald-600" /></div>
                                <div>
                                    <p className="text-sm font-semibold text-emerald-700">{customerName}</p>
                                    {customerInfo && <p className="text-xs text-emerald-500">⭐ {customerInfo.loyaltyPoints?.toLocaleString()} points</p>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Multi-Cart Tabs Bar */}
            {renderCartTabsBar()}

            {/* Cart Header */}
            <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-lg bg-emerald-100"><ShoppingCart size={16} className="text-emerald-600" /></div>
                        <div>
                            <h3 className="font-bold text-sm text-slate-900">{cartItemCount} {cartItemCount === 1 ? 'Item' : 'Items'}</h3>
                            {cart.length > 8 && <p className="text-[10px] text-slate-400">{cart.length} lines</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={clsx('text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider',
                            saleType === 'wholesale' ? 'bg-blue-100 text-blue-700' : saleType === 'clearance' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700')}>{saleType}</span>
                        {customerInfo && (
                            <div className={clsx('px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r', tierColors[tier])}>🥇 {tier}</div>
                        )}
                        {cart.length > 8 && (
                            <button onClick={() => setCompactView(!compactView)}
                                className="p-1 rounded-lg text-slate-400 hover:text-slate-600" type="button" title={compactView ? 'Detailed view' : 'Compact view'}>
                                {compactView ? <ScrollText size={14} /> : <List size={14} />}
                            </button>
                        )}
                    </div>
                </div>
                {customerInfo && (
                    <div className="mt-2 flex items-center gap-3 text-xs flex-wrap">
                        <span className="text-slate-500 font-medium">{customerInfo.name}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                        <span className="text-amber-500 font-bold">⭐ {customerInfo.loyaltyPoints?.toLocaleString()} pts</span>
                        {customerInfo.storeCredit > 0 && (<><span className="w-1 h-1 rounded-full bg-slate-300" /><span className="text-blue-500 font-bold">₦{customerInfo.storeCredit?.toLocaleString()} credit</span></>)}
                        {customerVerification && (<><span className="w-1 h-1 rounded-full bg-slate-300" /><span className={clsx('inline-flex items-center gap-1 font-bold', customerVerification === 'verified' ? 'text-emerald-600' : customerVerification === 'pending' ? 'text-blue-600' : 'text-slate-400')}>{customerVerification === 'verified' ? <ShieldCheck size={10} /> : customerVerification === 'pending' ? <Loader2 size={10} className="animate-spin" /> : <Shield size={10} />}{customerVerification === 'verified' ? 'Verified' : customerVerification === 'pending' ? 'Verifying...' : 'Unverified'}</span></>)}
                    </div>
                )}
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: compactView ? 'calc(100vh - 520px)' : 'calc(100vh - 480px)' }}>
                <div className="divide-y divide-slate-50">
                    <AnimatePresence initial={false}>
                        {cart.map((item, idx) => (
                            compactView ? (
                                <CartItemCompact key={item.id} item={item}
                                    onUpdateQuantity={updateQuantity} onRemove={removeItem}
                                    onStartDiscount={startEditDiscount} editingDiscount={editingDiscount}
                                    discountInput={discountInput} onDiscountInputChange={setDiscountInput}
                                    onApplyDiscount={handleCustomDiscount} onCancelDiscount={() => setEditingDiscount(null)}
                                    maxQuantities={maxQuantities}
                                />
                            ) : (
                                <motion.div key={item.id}
                                    initial={{ opacity: 0, x: 30, height: 0 }}
                                    animate={{ opacity: 1, x: 0, height: 'auto' }}
                                    exit={{ opacity: 0, x: -30, height: 0 }}
                                    transition={{ duration: 0.25, ease: 'easeOut', delay: idx * 0.03 }}
                                    className="px-4 py-3 hover:bg-slate-50/80 transition-colors group">
                                    <div className="flex items-start gap-3">
                                        <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-sm">
                                            {item.imageUrl ? <img src={item.imageUrl} alt={item.productName} className="w-full h-full object-cover" /> : <span className="text-sm font-bold text-slate-400">{item.productName?.charAt(0)?.toUpperCase() || 'P'}</span>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between">
                                                <p className="text-sm font-semibold text-slate-900 truncate pr-2">{item.productName}</p>
                                                <button onClick={() => removeItem(item.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" type="button"><Trash2 size={14} /></button>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-0.5">{formatNaira(item.unitPrice)} / {item.uomAbbreviation || 'pc'}</p>
                                            {editingDiscount === item.id ? (
                                                <div className="flex items-center gap-1.5 mt-1.5">
                                                    <div className="relative"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">₦</span><input type="number" value={discountInput} onChange={(e) => setDiscountInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleCustomDiscount(item.id); if (e.key === 'Escape') setEditingDiscount(null); }} className="w-24 pl-6 pr-2 py-1.5 border-2 border-amber-300 rounded-lg text-xs font-bold focus:ring-2 focus:ring-amber-500/20" autoFocus /></div>
                                                    <button onClick={() => handleCustomDiscount(item.id)} className="px-2 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold" type="button">Apply</button>
                                                    <button onClick={() => setEditingDiscount(null)} className="px-2 py-1.5 bg-slate-200 rounded-lg text-xs" type="button">✕</button>
                                                </div>
                                            ) : (
                                                <button onClick={() => startEditDiscount(item.id, item.discountAmount || 0)} className="flex items-center gap-1 mt-1 text-xs transition-colors" type="button">
                                                    {item.discountAmount > 0 ? <span className="text-amber-600 font-semibold">Discount: -{formatNaira(item.discountAmount)}</span> : <span className="text-slate-400 hover:text-amber-600"><Percent size={11} className="inline mr-0.5" />Add discount</span>}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-2.5 pl-14">
                                        <div className="flex items-center gap-0.5 bg-slate-100 rounded-xl p-0.5">
                                            <button onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1))} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-white hover:text-rose-500 hover:shadow-sm transition-all" type="button"><Minus size={14} /></button>
                                            <span className="w-10 text-center text-sm font-bold text-slate-900 tabular-nums select-none">{item.quantity}</span>
                                            <button
                                                onClick={() => {
                                                    const maxQty = maxQuantities?.[item.productId];
                                                    if (maxQty === undefined || item.quantity < maxQty) {
                                                        updateQuantity(item.id, item.quantity + 1);
                                                    }
                                                }}
                                                disabled={maxQuantities?.[item.productId] !== undefined && item.quantity >= maxQuantities[item.productId]}
                                                className={clsx(
                                                    'w-8 h-8 flex items-center justify-center rounded-lg',
                                                    maxQuantities?.[item.productId] !== undefined && item.quantity >= maxQuantities[item.productId]
                                                        ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                                                        : 'text-slate-500 hover:bg-white hover:text-emerald-500 hover:shadow-sm transition-all'
                                                )}
                                                type="button"
                                            ><Plus size={14} /></button>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-slate-900 tabular-nums">{formatNaira(item.totalAmount || item.quantity * item.unitPrice - (item.discountAmount || 0))}</p>
                                            {item.quantity > 1 && <p className="text-[10px] text-slate-400">{formatNaira(item.unitPrice)} each</p>}
                                        </div>
                                    </div>
                                </motion.div>
                            )
                        ))}
                    </AnimatePresence>
                </div>
            </div>

            {/* Discount Presets Panel */}
            <AnimatePresence>
                {showDiscounts && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden border-t border-slate-200 bg-gradient-to-b from-amber-50/80 to-white flex-shrink-0">
                        <div className="px-4 py-3">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Quick Discounts</p>
                                <button onClick={() => setShowDiscounts(false)} className="p-1 rounded-lg text-slate-400 hover:text-slate-600" type="button"><X size={14} /></button>
                            </div>
                            {discountOptions.length === 0 ? (
                                <div className="text-center py-4 px-2 bg-amber-50/50 rounded-xl border border-amber-200/50">
                                    <p className="text-xs text-slate-600 font-semibold">No discounts configured</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">Configure discounts in Settings &gt; Discounts to use them here</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                                    {discountOptions.map((preset) => (
                                        <button key={preset.id} onClick={() => handleApplyPreset(preset)}
                                            className={clsx('flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all border', preset.bgLight, preset.borderLight, 'hover:shadow-sm hover:scale-[1.02]')} type="button">
                                            <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br', preset.gradient)}><preset.icon size={14} className="text-white" /></div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-slate-700 truncate">{preset.name}</p>
                                                <p className="text-[11px] font-bold text-amber-600">{preset.type === 'percentage' ? `${preset.maxAmount}% off` : `₦${preset.maxAmount?.toLocaleString()} off`}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Footer - Totals & Charge Button */}
            <div className="px-4 py-4 border-t-2 border-slate-200 bg-gradient-to-t from-slate-50/80 to-white flex-shrink-0 space-y-2">
                <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Subtotal ({cartItemCount} items)</span>
                    <span className="font-semibold text-slate-700 tabular-nums">{formatNaira(subtotal)}</span>
                </div>
                {discountTotal > 0 && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="flex justify-between text-xs">
                        <span className="text-amber-600 font-medium">Discount</span>
                        <span className="font-semibold text-amber-600 tabular-nums">-{formatNaira(discountTotal)}</span>
                    </motion.div>
                )}
                <div className="flex justify-between text-xs">
                    <span className="text-slate-500">VAT (%)</span>
                    <span className="font-semibold text-slate-700 tabular-nums">{formatNaira(taxTotal)}</span>
                </div>
                <div className="flex justify-between items-center pt-2.5 border-t border-slate-200">
                    <span className="text-sm font-bold text-slate-900">TOTAL</span>
                    <motion.span
                        key={grandTotal}
                        initial={{ scale: 1.2, color: '#059669' }}
                        animate={{ scale: 1, color: '#059669' }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                        className="text-xl font-black text-emerald-600 tabular-nums"
                    >
                        {formatNaira(grandTotal)}
                    </motion.span>
                </div>
                {customerInfo && grandTotal > 0 && (
                    <div className="flex items-center gap-1.5 text-[11px] text-amber-600">
                        <Sparkles size={12} />
                        <span>Earn {Math.floor(grandTotal / 100)} loyalty points</span>
                    </div>
                )}

                <div className="flex gap-2 pt-1">
                    <button
                        onClick={() => setShowDiscounts(!showDiscounts)}
                        className={clsx(
                            'flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold transition-all flex-1',
                            showDiscounts
                                ? 'bg-amber-100 text-amber-700 border-2 border-amber-200 shadow-sm'
                                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                        )}
                        type="button"
                    >
                        <Percent size={14} /> Discounts
                    </button>
                </div>

                {/* Walk-in Sale Info - Informational, Not Blocking */}
                {!customerId && cart.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2"
                    >
                        <AlertCircle size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-xs font-semibold text-blue-700">Walk-in Sale</p>
                            <p className="text-[10px] text-blue-600">
                                No customer attached. Sale will be processed as walk-in.
                            </p>
                        </div>
                    </motion.div>
                )}

                {/* Charge Button - Clear States, Always Works */}
                <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={onCheckout}
                    disabled={disabled || cart.length === 0}
                    className={clsx(
                        'flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-bold text-sm shadow-lg transition-all',
                        'bg-gradient-to-r from-emerald-600 to-teal-600 text-white',
                        'hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl hover:shadow-emerald-500/25',
                        'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none'
                    )}
                    type="button"
                >
                    <Receipt size={16} />
                    {cart.length === 0
                        ? 'Cart is Empty'
                        : disabled
                            ? 'Processing...'
                            : `Charge ${formatNaira(grandTotal)}`}
                </motion.button>

                {/* Keyboard shortcut hint */}
                {!disabled && cart.length > 0 && (
                    <p className="text-center text-[10px] text-slate-400">
                        Press <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-[9px] font-mono">Enter</kbd> in payment modal to quick-charge
                    </p>
                )}
            </div>
        </div>
    );
}